import { Injectable, inject } from '@angular/core';

import { distinctUntilChanged } from 'rxjs';
import { datadogRum } from '@datadog/browser-rum';

import { ConsentTextId } from '@fcom/common/interfaces/consents.interface';
import { ConsentService } from '@fcom/common/login/services/consent.service';
import { startDatadogClientMonitoring } from '@fcom/core/services/monitoring/monitoring-adapter.module';
import { clearDatadogCookies } from '@fcom/core/services/monitoring/datadog-cookies';
import { jsConfig } from '@fcom/core/services/config/jsConfig';

/**
 * Puts Datadog RUM behind analytics-cookie consent. RUM is not initialised at bootstrap; this
 * service decides, from the customer's `f-cookies-analytics` consent, both *whether* and *how* it
 * starts:
 *
 * - **granted** → start in cookie-backed mode and record Session Replay.
 * - **not granted**, `inMemoryBeforeConsent` on (default) → start in cookieless in-memory mode so we
 *   still get errors/performance without writing any cookie or storage. Replay stays off.
 * - **not granted**, `inMemoryBeforeConsent` off → do not start at all until consent is granted.
 *
 * Persistence is fixed at init and the SDK cannot re-init, so the mode is chosen once per page load
 * from the first (synchronously seeded) consent value. Later changes are handled at runtime:
 * granting starts replay, withdrawing stops replay, halts collection via `setTrackingConsent`, and
 * clears the Datadog cookies.
 */
@Injectable({ providedIn: 'root' })
export class DatadogConsentService {
  private consentService = inject(ConsentService);
  private started = false;

  gateOnConsent(): void {
    const inMemoryBeforeConsent = jsConfig.datadog?.inMemoryBeforeConsent ?? true;

    this.consentService
      .getCookieConsentStatusById(ConsentTextId.COOKIE_ANALYTICS)
      .pipe(distinctUntilChanged())
      .subscribe((granted) => {
        // `null` means the customer hasn't accepted or declined analytics yet (consent popup shown).
        // Wait for an explicit choice before deciding whether/how to start Datadog.
        if (granted === null) {
          return;
        }

        if (!this.started) {
          if (granted) {
            startDatadogClientMonitoring('cookie');
            this.started = true;
            datadogRum.startSessionReplayRecording();
          } else if (inMemoryBeforeConsent) {
            clearDatadogCookies();
            startDatadogClientMonitoring('memory');
            this.started = true;
          }
          return;
        }

        if (granted) {
          datadogRum.setTrackingConsent('granted');
          datadogRum.startSessionReplayRecording();
        } else {
          datadogRum.stopSessionReplayRecording();
          datadogRum.setTrackingConsent('not-granted');
          clearDatadogCookies();
        }
      });
  }
}
